// LiftAi is an anonymous buyer-intent provider — unlike 6Sense / Bombora /
// Leadfeeder which return company-level deanonymization. The customer's site
// embeds LiftAi's tag, which writes two first-party cookies and dispatches
// browser events on `document` whenever the score is (re)computed.
//
// Cookies (per LiftAi partner endpoint doc):
//   vs_conv_ai  → score band string, e.g. "15-19", "60-64" (0-99 in 5pt bands)
//   vs_intent   → segment string, "LowIntent" / "MidIntent" / "HighIntent"
//
// Events (per partner endpoint doc, dispatched on `document`):
//   LiftAICookiesSet       fires every time score cookies are (re)written
//   LiftAIScoreBandChange  fires when the score band changes mid-session
//   LiftAISegmentChange    fires when the segment changes mid-session
//
// Race-condition handling: LiftAi's docs warn against reading cookies on DOM
// ready because they may not be populated yet. The cookies *are* written
// synchronously before each event dispatch in their snippet, so once a value
// is present in the cookie we can safely read it. We do a catch-up read on
// subscribe to handle the case where their script loaded before ours and the
// initial LiftAICookiesSet fired before our listener was attached.

type LiftAiData = {
  vs_intent: string | null
  vs_conv_ai: string | null
}

export type TLiftAiDeanonymizationEvent = {
  provider: 'LiftAi'
  data: LiftAiData
}

const COOKIE_SCORE = 'vs_conv_ai'
const COOKIE_INTENT = 'vs_intent'

const EVENT_NAMES = ['LiftAICookiesSet', 'LiftAIScoreBandChange', 'LiftAISegmentChange'] as const

const readCookie = (name: string): string | null => {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`))
  return match ? decodeURIComponent(match[1]) : null
}

const readLiftAiData = (): LiftAiData => ({
  vs_intent: readCookie(COOKIE_INTENT),
  vs_conv_ai: readCookie(COOKIE_SCORE),
})

const hasData = (data: LiftAiData) => data.vs_intent !== null || data.vs_conv_ai !== null

export const subscribeToLiftAi = (
  onEvent: (event: TLiftAiDeanonymizationEvent) => void
): (() => void) => {
  let lastSerialized: string | null = null

  const emitIfChanged = () => {
    const data = readLiftAiData()
    if (!hasData(data)) {
      // LiftAi hasn't scored this visitor yet — cookies absent. Wait for the
      // next event.
      return
    }
    const serialized = JSON.stringify(data)
    if (serialized === lastSerialized) {
      return
    }
    lastSerialized = serialized
    onEvent({ provider: 'LiftAi', data })
  }

  EVENT_NAMES.forEach(name => {
    document.addEventListener(name, emitIfChanged)
  })

  // Catch-up: if LiftAi fired its events before we attached, the cookies are
  // already populated. Read once now to recover the state.
  emitIfChanged()

  return () => {
    EVENT_NAMES.forEach(name => {
      document.removeEventListener(name, emitIfChanged)
    })
  }
}
